export const createImage = (url: string): Promise<unknown> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

/**
 * Returns the angle between two points.
 * @param {number} degreeValue - The degree value
 * @returns {number} - The radian angle
 */
export function getRadianAngle(degreeValue: number): number {
  return (degreeValue * Math.PI) / 180;
}

/**
 * @description Returns the size of the image after rotation.
 * @param {number} width - The width of the image
 * @param {number} height - The height of the image
 * @param {number} rotation - The rotation angle
 * @returns {object} - The size of the image after rotation
 */
export function rotateSize(
  width: number,
  height: number,
  rotation: number,
): {
  width: number;
  height: number;
} {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

type TPixelCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * @description This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * @param {string} imageSrc - The image source
 * @param {TPixelCrop} pixelCrop - The pixel crop
 * @param {number} rotation - The rotation angle
 * @param {object} flip - The flip object
 * @returns {Promise<string>} - The cropped image as a blob
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: TPixelCrop,
  rotation: number = 0,
  flip = { horizontal: false, vertical: false },
): Promise<string | null> {
  const image: HTMLImageElement = (await createImage(
    imageSrc,
  )) as HTMLImageElement;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  return new Promise((resolve, _reject) => {
    croppedCanvas.toBlob((file) => {
      if (file) {
        resolve(URL.createObjectURL(file));
      }
    }, "image/png");
  });
}

/**
 * @description This function was adapted from the one in the ReadMe of
 * @param {string} imageSrc - The image source
 * @param {number} rotation - The rotation angle
 * @returns {Promise<string>} - The rotated image as a blob
 */
export async function getRotatedImage(
  imageSrc: string,
  rotation = 0,
): Promise<string> {
  const image: HTMLImageElement = (await createImage(
    imageSrc,
  )) as HTMLImageElement;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }
  if (ctx) {
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(image, -image.width / 2, -image.height / 2);
  }

  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      if (file) {
        resolve(URL.createObjectURL(file));
      }
    }, "image/png");
  });
}
